/* eslint-disable */
import axios from 'axios'
import Cookies from 'js-cookie'
import { requestStarted, requestEnded } from './loadingBridge'
import { GLOBAL_LOADING_ON_GET } from './loadingPolicy'
import { clearCurrentScreenId, getCurrentScreenId } from '../utils/screenId'
import { clearScreens } from '../utils/screens/screenStorage'

const baseURL = process.env.REACT_APP_BASE_URL

const routesWithoutLoading = [
  'academic_years',
  'stages',
  'shifts',
  'departments',
  'my-screens',
  'my-user',
  'student_change_group_context',
  'variables',
  'student-statistics-dashboard',
  'finance_reports/summary',
  'finance_reports/revenue',
  'finance_reports/revenue_stats',
  'finance_reports/receipt_print',
  'finance_reports/students',
  'finance_reports/installment_stats',
  'finance_reports/exemptions',
  'finance_reports/exemption_stats',
  'finance_reports/outstanding_by_stage',
  'professor-statistics-dashboard',
  'student-statistics-report',
  'graduate-statistics-report',
  'graduate-statistics-years',
  'student-names-report',
  'student-names-report/filters',
  'do_documents',
  'assign_screens',
  'assign_screens/copy',
  'screens',
  'role_groups',
  'roles',
  'users',
]

const getRouteBase = (url = '') => String(url).split('?')[0]

const AUTH_ROUTES = [
  'users-login',
  'users-confirm',
  'users-forgot-password',
  'users-logout',
  'user-logout',
]

const isAuthRoute = (url = '') => {
  const base = getRouteBase(url)
  return AUTH_ROUTES.some((route) => base === route || base.endsWith(`/${route}`))
}

let redirectingToLogin = false

const redirectUnauthorizedToLogin = () => {
  const hashPath = window.location.hash.replace(/^#/, '')
  if (hashPath.startsWith('/login')) return
  if (redirectingToLogin) return

  redirectingToLogin = true
  Cookies.remove('token')
  Cookies.remove('user_data')
  clearCurrentScreenId()
  clearScreens()
  try {
    localStorage.removeItem('userData')
  } catch {
    // ignore storage cleanup failures
  }

  ;[getInstance, deleteInstance, updateInstance, addInstance, patchInstance].forEach((instance) => {
    if (instance?.defaults?.headers?.common) {
      delete instance.defaults.headers.common['x-access-token']
    }
  })

  window.location.hash = '#/login'
  window.setTimeout(() => {
    redirectingToLogin = false
  }, 1500)
}

// A 401 for a token that is no longer the saved one comes from a tab left open with an older (expired) login
// while another tab has logged in since: that tab takes the newer login and reloads, instead of clearing the
// shared cookies and logging the other tabs out.
let adoptingNewerLogin = false

const sentTokenOf = (config) => {
  const headers = config?.headers
  return (
    (typeof headers?.get === 'function' ? headers.get('x-access-token') : null) ??
    headers?.['x-access-token']
  )
}

const adoptNewerLogin = (token) => {
  if (adoptingNewerLogin) return
  adoptingNewerLogin = true
  ;[getInstance, deleteInstance, updateInstance, addInstance, patchInstance].forEach((instance) => {
    if (instance?.defaults?.headers?.common)
      instance.defaults.headers.common['x-access-token'] = token
  })
  window.location.reload()
}

const attachUnauthorizedInterceptor = (instance) => {
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status
      const url = error?.config?.url || ''
      if (status === 401 && !isAuthRoute(url)) {
        const current = Cookies.get('token')
        const sent = sentTokenOf(error?.config)
        if (current && sent && current !== sent) adoptNewerLogin(current)
        else redirectUnauthorizedToLogin()
      }
      return Promise.reject(error)
    },
  )
}

// Backend errors arrive as { success: false, Error: { message } }; most screens read res.message.
// Copy the backend text to message, and turn non-JSON gateway pages (502/504 HTML) into a readable error.
const attachErrorMessageInterceptor = (instance) => {
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const response = error?.response
      if (response) {
        const body = response.data
        if (body && typeof body === 'object' && !Array.isArray(body)) {
          if (!body.message) {
            const backendMessage = body.Error?.message || body.error?.message || body.data?.message
            if (backendMessage) body.message = backendMessage
          }
        } else {
          response.data = {
            success: false,
            message: `تعذر الاتصال بالخادم (HTTP ${response.status}). يرجى المحاولة مرة أخرى.`,
          }
        }
      }
      return Promise.reject(error)
    },
  )
}

const shouldSkipLoading = (config) => {
  if (config?.skipLoading) return true
  if (config?.showGlobalLoading) return false
  return routesWithoutLoading.includes(getRouteBase(config?.url))
}

const attachScreenIdInterceptor = (instance) => {
  instance.interceptors.request.use((config) => {
    const screenId = getCurrentScreenId()
    if (screenId != null && !config.headers?.['screen-id']) {
      config.headers = { ...config.headers, 'screen-id': screenId }
    }
    return config
  })
}

const attachLoadingInterceptors = (instance) => {
  instance.interceptors.request.use(
    (config) => {
      if (!shouldSkipLoading(config)) {
        requestStarted()
      }
      return config
    },
    (error) => {
      if (!shouldSkipLoading(error.config)) {
        requestEnded()
      }
      return Promise.reject(error)
    },
  )

  instance.interceptors.response.use(
    (response) => {
      if (!shouldSkipLoading(response.config)) {
        requestEnded()
      }
      return response
    },
    (error) => {
      if (!shouldSkipLoading(error.config)) {
        requestEnded()
      }
      return Promise.reject(error)
    },
  )
}

const myCookie = Cookies.get('token')

export const deleteInstance = axios.create({
  baseURL: baseURL,
  method: 'DELETE',
})

export const getInstance = axios.create({
  baseURL: baseURL,
  method: 'GET',
})

export const updateInstance = axios.create({
  baseURL: baseURL,
  method: 'PUT',
})

export const addInstance = axios.create({
  baseURL: baseURL,
  method: 'POST',
})

export const patchInstance = axios.create({
  baseURL: baseURL,
  method: 'PATCH',
})

attachScreenIdInterceptor(getInstance)
attachUnauthorizedInterceptor(getInstance)
attachErrorMessageInterceptor(getInstance)

if (GLOBAL_LOADING_ON_GET) {
  attachLoadingInterceptors(getInstance)
}

;[deleteInstance, updateInstance, addInstance, patchInstance].forEach((instance) => {
  attachLoadingInterceptors(instance)
  attachScreenIdInterceptor(instance)
  attachUnauthorizedInterceptor(instance)
  attachErrorMessageInterceptor(instance)
})

if (myCookie) {
  getInstance.defaults.headers.common['x-access-token'] = myCookie
  deleteInstance.defaults.headers.common['x-access-token'] = myCookie
  updateInstance.defaults.headers.common['x-access-token'] = myCookie
  addInstance.defaults.headers.common['x-access-token'] = myCookie
  patchInstance.defaults.headers.common['x-access-token'] = myCookie
}
